import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import React, { FC, ReactNode, useCallback, useMemo, useState } from 'react';

import {
  type BankAccountListBankAccountsResponse,
  type BankAccountWithDetails,
  bankAccountGetBankAccount,
  bankAccountListBankAccounts,
} from '@column/api';
import { Dropdown, DropdownOption, Icon, Tooltip, formatNumber } from '@column/column-ui-kit';

import { useDebounce } from '~/hooks/useDebounce';
import {
  type BankAccountDropdownFilters,
  bankAccountDropdownListQueryKey,
} from '~/lib/api/bankAccounts/bankAccountDropdownQueryKey';
import { type Action } from '~/lib/authz/eval';
import {
  type HighlightSuffixCandidate,
  getHighlightedField,
  getHighlightedSuffix,
  isFragmentTailHighlighted,
  renderHighlight,
  stripEmTags,
} from '~/lib/search/highlight';
import { useSessionStore } from '~/stores/Session';

export type BankAccountFilters = BankAccountDropdownFilters;

// --- Label components ---

const SUFFIX_CANDIDATES: HighlightSuffixCandidate[] = [
  { field: 'ACCOUNT_NUMBERS.ROUTING_NUMBER', label: 'Rtn#' },
  { field: 'ACCOUNT_NUMBERS.DESCRIPTION', label: 'Acc# Description' },
  { field: 'ACCOUNT_NUMBERS.ACCOUNT_NUMBER_ID', label: 'Acc# ID' },
  { field: 'BANK_ACCOUNT_ID', label: 'ID' },
];

const HighlightedLabel: FC<{
  bankAccount: BankAccountWithDetails;
  accountPreview?: ReactNode;
}> = ({ bankAccount, accountPreview }) => {
  const highlightedDisplayName = getHighlightedField(bankAccount.search_metadata, 'DISPLAY_NAME');
  const highlightedDescription = getHighlightedField(bankAccount.search_metadata, 'DESCRIPTION');
  const displayPart = highlightedDisplayName ? renderHighlight(highlightedDisplayName) : bankAccount.display_name;
  const descriptionPart = highlightedDescription ? renderHighlight(highlightedDescription) : bankAccount.description;

  if (accountPreview) {
    return displayPart ? (
      <>
        {displayPart} {accountPreview}
        {descriptionPart ? <> — {descriptionPart}</> : ''}
      </>
    ) : (
      <>
        {descriptionPart || 'Unnamed'} {accountPreview}
      </>
    );
  }

  if (displayPart && descriptionPart) {
    return (
      <>
        {displayPart} – {descriptionPart}
      </>
    );
  }
  return <>{descriptionPart || 'Unnamed'}</>;
};

const BankAccountOptionSmall: FC<{ bankAccount: BankAccountWithDetails }> = ({ bankAccount }) => {
  const balance = formatNumber(bankAccount.balances?.available_amount ?? '');
  // When DESCRIPTION is highlighted in the main label, drop ACCOUNT_NUMBERS.DESCRIPTION
  // from the suffix to avoid showing the same highlighted value twice.
  const descriptionHighlighted = Boolean(getHighlightedField(bankAccount.search_metadata, 'DESCRIPTION'));
  const candidates = descriptionHighlighted
    ? SUFFIX_CANDIDATES.filter((c) => c.field !== 'ACCOUNT_NUMBERS.DESCRIPTION')
    : SUFFIX_CANDIDATES;
  const suffix = getHighlightedSuffix(bankAccount.search_metadata, candidates);
  if (!suffix) return <>{balance}</>;
  return (
    <>
      {balance}
      {' · '}
      {suffix.label}: {renderHighlight(suffix.fragment)}
    </>
  );
};

const PlainLabel: FC<{ displayName?: string; description?: string | null; accountPreview?: string }> = ({
  displayName,
  description,
  accountPreview,
}) => {
  if (accountPreview) {
    return (
      <>
        {displayName
          ? `${displayName} ${accountPreview}${description ? ` — ${description}` : ''}`
          : `${description || 'Unnamed'} ${accountPreview}`}
      </>
    );
  }
  if (displayName && description) return <>{`${displayName} – ${description}`}</>;
  return <>{description || 'Unnamed'}</>;
};

const highlightedAccountPreview = (bankAccount: BankAccountWithDetails, lastFour: string): ReactNode => {
  const defaultNumber = bankAccount.default_account_number;
  const fragments = bankAccount.search_metadata?.highlights?.['ACCOUNT_NUMBERS.ACCOUNT_NUMBER'];
  if (!defaultNumber || !fragments?.length) return `(...${lastFour})`;
  const match = fragments.find((f) => stripEmTags(f) === defaultNumber);
  if (!match || !isFragmentTailHighlighted(match, lastFour.length)) return `(...${lastFour})`;
  return renderHighlight(`(...<em>${lastFour}</em>)`);
};

const BankAccountOptionLabel: FC<{
  bankAccount: BankAccountWithDetails;
  highlightSearch: boolean;
  hasSearchQuery: boolean;
}> = ({ bankAccount, highlightSearch, hasSearchQuery }) => {
  const lastFour = bankAccount.default_account_number?.slice(-4);

  if (hasSearchQuery && highlightSearch && bankAccount.search_metadata?.highlights) {
    const preview = lastFour ? highlightedAccountPreview(bankAccount, lastFour) : undefined;
    return <HighlightedLabel bankAccount={bankAccount} accountPreview={preview} />;
  }
  const accountPreview = lastFour ? `(...${lastFour})` : undefined;
  return (
    <PlainLabel
      displayName={bankAccount.display_name}
      description={bankAccount.description}
      accountPreview={accountPreview}
    />
  );
};

// --- Shared hook ---

interface UseBankAccountOptionsConfig {
  value?: string | null;
  filters?: BankAccountFilters;
}

export const useBankAccountOptions = (config: UseBankAccountOptionsConfig = {}) => {
  const { value, filters } = config;
  const platformId = useSessionStore((state) => state.currentPlatform?.id);
  const isSandbox = useSessionStore((state) => state.isSandbox);
  const [searchText, setSearchText] = useState<string | undefined>();
  const effectiveQuery = useMemo(() => {
    if (searchText) return { ...filters, query: searchText };
    return filters;
  }, [filters, searchText]);

  const bankAccountsQuery = useInfiniteQuery({
    queryKey: bankAccountDropdownListQueryKey(platformId, isSandbox, effectiveQuery),
    queryFn: async ({ pageParam, signal }) => {
      const startingAfter = typeof pageParam === 'string' ? pageParam : undefined;
      const { data } = await bankAccountListBankAccounts({
        query: { ...effectiveQuery, starting_after: startingAfter },
        signal,
        throwOnError: true,
      });
      return data as BankAccountListBankAccountsResponse;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage?.has_more) return undefined;
      return lastPage.bank_accounts?.[lastPage.bank_accounts.length - 1]?.id;
    },
    initialPageParam: undefined as string | undefined,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });

  const resolvedId = value ?? undefined;

  const bankAccounts = useMemo(() => {
    const entries = bankAccountsQuery.data?.pages.flatMap((page) => page?.bank_accounts ?? []) ?? [];
    return [...new Map(entries.map((entry) => [entry.id, entry])).values()];
  }, [bankAccountsQuery.data]);

  const activeBankAccountQuery = useQuery({
    queryKey: ['bankAccountDropdown', 'get', platformId, isSandbox, resolvedId],
    queryFn: async () => {
      const { data } = await bankAccountGetBankAccount({
        path: { bank_account_id: resolvedId! },
        throwOnError: true,
      });
      return data;
    },
    enabled: Boolean(resolvedId),
    placeholderData: () => bankAccounts.find((ba) => ba.id === resolvedId),
    staleTime: 5 * 60_000, // 5 minutes
  });

  const activeBankAccount = activeBankAccountQuery.data as BankAccountWithDetails | undefined;
  const hasSearchQuery = Boolean(searchText);

  // Map each loaded account to its owner entity IDs so authz checks can resolve entity-level
  // permission overrides. Covers both the paginated list and the currently-selected account.
  const ownersById = useMemo<Record<string, string[] | undefined>>(() => {
    const map: Record<string, string[] | undefined> = {};
    bankAccounts.forEach((ba) => {
      map[ba.id] = ba.owners;
    });
    if (activeBankAccount?.id) {
      map[activeBankAccount.id] = activeBankAccount.owners;
    }
    return map;
  }, [bankAccounts, activeBankAccount]);

  const [search] = useDebounce((query?: string) => setSearchText(query || undefined), 300, []);

  const toOption = useCallback(
    (ba: BankAccountWithDetails, highlightSearch: boolean): DropdownOption => ({
      label: (
        <BankAccountOptionLabel bankAccount={ba} highlightSearch={highlightSearch} hasSearchQuery={hasSearchQuery} />
      ),
      small:
        hasSearchQuery && highlightSearch && ba.search_metadata?.highlights ? (
          <BankAccountOptionSmall bankAccount={ba} />
        ) : (
          `${formatNumber(ba.balances?.available_amount ?? '')}`
        ),
      value: ba.id,
    }),
    [hasSearchQuery]
  );

  const options: DropdownOption[] = useMemo(
    () => bankAccounts.map((ba) => toOption(ba, true)),
    [bankAccounts, toOption]
  );

  const hiddenOptions = useMemo(() => {
    if (!resolvedId) return [];

    const selected =
      bankAccounts.find((a) => a.id === resolvedId) ||
      (activeBankAccount?.id === resolvedId ? activeBankAccount : undefined);

    return selected ? [toOption(selected, false)] : [];
  }, [activeBankAccount, resolvedId, bankAccounts, toOption]);

  const { fetchNextPage, hasNextPage, isFetchingNextPage, isPlaceholderData } = bankAccountsQuery;
  const handleScrollEnd = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage && !isPlaceholderData) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isPlaceholderData]);

  return {
    options,
    hiddenOptions,
    search,
    handleScrollEnd,
    hasSearchQuery,
    bankAccounts,
    ownersById,
  };
};

// --- Component ---

export interface BankAccountDropdownProps {
  value?: string | null;
  onChange: (value: string) => void;
  permission?: Action['permission'];
  unauthorizedText?: string;
  filters?: BankAccountFilters;
  excludeBankAccountIds?: string[];
  fullWidth?: boolean;
  maxWidth?: string;
  hasError?: boolean;
  placeholder?: string;
  isDisabled?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const BankAccountDropdown: FC<BankAccountDropdownProps> = ({
  value,
  onChange,
  permission,
  unauthorizedText,
  filters,
  excludeBankAccountIds,
  fullWidth,
  maxWidth,
  hasError,
  placeholder,
  isDisabled,
  onOpenChange,
}) => {
  const { isAuthorized } = useSessionStore();

  const {
    options: baseOptions,
    hiddenOptions: baseHiddenOptions,
    search,
    handleScrollEnd,
    ownersById,
  } = useBankAccountOptions({
    value,
    filters,
  });

  const excludeSet = useMemo(
    () => (excludeBankAccountIds?.length ? new Set(excludeBankAccountIds) : undefined),
    [excludeBankAccountIds]
  );

  const enrichOption = useCallback(
    (opt: DropdownOption): DropdownOption => {
      if (!permission && !unauthorizedText) return opt;

      const isBankAccountAuthorized = !permission
        ? true
        : isAuthorized({
            permission,
            permissionLevel: 'write',
            bankAccount: { id: opt.value, owners: ownersById[opt.value] },
          });

      return {
        ...opt,
        suffix:
          !isBankAccountAuthorized && unauthorizedText ? (
            <Tooltip content={unauthorizedText} placement="right" zIndex={15}>
              <Icon.AnimationLock />
            </Tooltip>
          ) : undefined,
        isDisabled: !isBankAccountAuthorized,
      };
    },
    [isAuthorized, permission, unauthorizedText, ownersById]
  );

  const options = useMemo(
    () => baseOptions.filter((o) => !excludeSet || !excludeSet.has(o.value)).map(enrichOption),
    [baseOptions, excludeSet, enrichOption]
  );

  const hiddenOptions = useMemo(
    () => baseHiddenOptions.filter((o) => !excludeSet || !excludeSet.has(o.value)).map(enrichOption),
    [baseHiddenOptions, excludeSet, enrichOption]
  );

  return (
    <Dropdown
      options={options}
      hiddenOptions={hiddenOptions}
      active={value ?? undefined}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      emptyMessage="No accounts found"
      onSearchChange={(description: string) => search(description)}
      onChange={onChange}
      onOpenChange={onOpenChange}
      onScrollEnd={handleScrollEnd}
      hasError={hasError}
      isDisabled={isDisabled}
      placeholder={placeholder}
      search
    />
  );
};
